import { useMemo, useState } from 'react'
import { useDirty } from '@/hooks/useDirty'
import { Edit, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { Combobox } from '@/components/Combobox'
import { CurrencyCombobox } from '@/components/CurrencyCombobox'
import { NumberStepper } from '@/components/NumberStepper'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import {
  useApprovalCatalog,
  useApprovalPolicies,
  useCreateApprovalPolicy,
  useDeleteApprovalPolicy,
  useUpdateApprovalPolicy,
} from '@/features/approvals/queries'
import { useRoles } from '@/features/team/queries'
import type {
  ApprovalPolicy,
  ApprovalPolicyPayload,
  ApprovalResourceType,
  ApprovalStepConfig,
} from '@/features/approvals/types'
import { formatDateTime } from '@/lib/locale'

type EditingState = null | { mode: 'create' } | { mode: 'edit'; policy: ApprovalPolicy }

function blankPayload(resourceTypes: ApprovalResourceType[]): ApprovalPolicyPayload {
  return {
    name: '',
    description: '',
    resource_type: resourceTypes[0] || 'invoice.post',
    is_active: true,
    trigger_conditions: {},
    approval_pattern: 'sequential',
    steps_config: [{ role: 'cfo', required_count: 1, parallel: false }],
    sla_hours: null,
    sla_action: 'escalate',
    self_approval_allowed: false,
    priority: 100,
  }
}

function stepsSummary(steps: ApprovalStepConfig[]) {
  if (!steps || steps.length === 0) return <span className="text-muted-foreground">-</span>
  return (
    <span className="text-xs">
      {steps.map((s, i) => (
        <span key={i}>
          {i > 0 ? <span className="text-muted-foreground"> → </span> : null}
          {s.role ? `${s.role}` : `user ${s.user_id}`}
          {s.required_count > 1 ? <span className="text-muted-foreground"> ×{s.required_count}</span> : null}
        </span>
      ))}
    </span>
  )
}

function conditionsSummary(cond: ApprovalPolicy['trigger_conditions']) {
  const parts: string[] = []
  if (cond.amount_min !== undefined) parts.push(`min ${cond.amount_min}`)
  if (cond.amount_max !== undefined) parts.push(`max ${cond.amount_max}`)
  if (cond.currency) parts.push(cond.currency)
  if (cond.new_customer_only) parts.push('new customer')
  if (cond.bank_changed_within_days !== undefined) parts.push(`bank ±${cond.bank_changed_within_days}d`)
  return parts.length ? parts.join(', ') : <span className="text-muted-foreground">(any)</span>
}

export function ApprovalPoliciesPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('approvals.policies.manage')
  const { toast } = useToast()

  const catalog = useApprovalCatalog()
  const resourceTypes = (catalog.data?.resource_types || []) as ApprovalResourceType[]
  const approvalPatterns = catalog.data?.approval_patterns || []
  const slaActions = catalog.data?.sla_actions || []

  const rolesQ = useRoles()
  const roleOptions = useMemo(
    () =>
      (rolesQ.data?.roles ?? [])
        .map((r) => ({ value: r.name, label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [rolesQ.data],
  )

  const { data, isLoading } = useApprovalPolicies()
  const createMut = useCreateApprovalPolicy()
  const updateMut = useUpdateApprovalPolicy()
  const deleteMut = useDeleteApprovalPolicy()

  const [editing, setEditing] = useState<EditingState>(null)
  const [form, setForm] = useState<ApprovalPolicyPayload>(blankPayload(resourceTypes))
  const [deleteTarget, setDeleteTarget] = useState<ApprovalPolicy | null>(null)

  const openCreate = () => {
    setForm(blankPayload(resourceTypes))
    setEditing({ mode: 'create' })
  }
  const openEdit = (p: ApprovalPolicy) => {
    setForm({
      name: p.name,
      description: p.description || '',
      resource_type: p.resource_type,
      is_active: p.is_active,
      trigger_conditions: p.trigger_conditions || {},
      approval_pattern: p.approval_pattern,
      steps_config: p.steps_config,
      sla_hours: p.sla_hours,
      sla_action: p.sla_action,
      self_approval_allowed: p.self_approval_allowed,
      priority: p.priority,
    })
    setEditing({ mode: 'edit', policy: p })
  }

  function setField<K extends keyof ApprovalPolicyPayload>(k: K, v: ApprovalPolicyPayload[K]) {
    setForm((prev) => ({ ...prev, [k]: v }))
  }

  // Dirty-check the editor modal. For "edit" the baseline mirrors the
  // hydrate block in openEdit(); for "create" it mirrors blankPayload()
  // so a brand-new modal stays disabled until the user has typed at
  // least the policy name or tweaked a step.
  const policyBaseline = useMemo<ApprovalPolicyPayload | null>(() => {
    if (!editing) return null
    if (editing.mode === 'edit') {
      const p = editing.policy
      return {
        name: p.name,
        description: p.description || '',
        resource_type: p.resource_type,
        is_active: p.is_active,
        trigger_conditions: p.trigger_conditions || {},
        approval_pattern: p.approval_pattern,
        steps_config: p.steps_config,
        sla_hours: p.sla_hours,
        sla_action: p.sla_action,
        self_approval_allowed: p.self_approval_allowed,
        priority: p.priority,
      }
    }
    return blankPayload(resourceTypes)
  }, [editing, resourceTypes])
  const policyDirty = useDirty(form, policyBaseline)

  function setStep(i: number, patch: Partial<ApprovalStepConfig>) {
    setForm((prev) => ({
      ...prev,
      steps_config: prev.steps_config.map((s, idx) => idx === i ? { ...s, ...patch } : s),
    }))
  }
  function addStep() {
    setForm((prev) => ({
      ...prev,
      steps_config: [...prev.steps_config, { role: 'admin', required_count: 1, parallel: false }],
    }))
  }
  function removeStep(i: number) {
    setForm((prev) => ({
      ...prev,
      steps_config: prev.steps_config.filter((_, idx) => idx !== i),
    }))
  }

  async function onSave() {
    if (!form.name?.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return }
    if (form.steps_config.length === 0) { toast({ title: 'At least one approval step is required', variant: 'destructive' }); return }
    try {
      if (editing && editing.mode === 'edit') {
        await updateMut.mutateAsync({ id: editing.policy.id, body: form })
        toast({ title: 'Policy updated' })
      } else {
        await createMut.mutateAsync(form)
        toast({ title: 'Policy created' })
      }
      setEditing(null)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Save failed', variant: 'destructive' })
    }
  }

  async function onDeleteConfirm() {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync(deleteTarget.id)
      toast({ title: 'Policy deactivated' })
      setDeleteTarget(null)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Delete failed', variant: 'destructive' })
    }
  }

  const columns = useMemo(() => [
    { accessorKey: 'name', header: 'Name',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          {row.original.description ? (
            <div className="text-xs text-muted-foreground">{row.original.description}</div>
          ) : null}
        </div>
      ),
    },
    { accessorKey: 'resource_type', header: 'Resource',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) => (
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.original.resource_type}</code>
      ),
    },
    { accessorKey: 'trigger_conditions', header: 'When',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) => conditionsSummary(row.original.trigger_conditions),
    },
    { accessorKey: 'steps_config', header: 'Ladder',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) => stepsSummary(row.original.steps_config),
    },
    { accessorKey: 'sla_hours', header: 'SLA',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) =>
        row.original.sla_hours ? `${row.original.sla_hours}h → ${row.original.sla_action}` : <span className="text-muted-foreground">-</span>,
    },
    { accessorKey: 'priority', header: 'Prio' },
    { accessorKey: 'is_active', header: 'Status',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) =>
        row.original.is_active ? <Badge variant="success">active</Badge> : <Badge variant="secondary">inactive</Badge>,
    },
    { accessorKey: 'updated_at', header: 'Updated',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) => formatDateTime(row.original.updated_at),
    },
    { accessorKey: 'actions', header: '',
      cell: ({ row }: { row: { original: ApprovalPolicy } }) => (
        canManage ? (
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="size-7" onClick={() => openEdit(row.original)} aria-label="Edit">
              <Edit className="size-4" />
            </Button>
            <Button size="icon" variant="ghost" className="size-7" onClick={() => setDeleteTarget(row.original)} aria-label="Deactivate">
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </div>
        ) : null
      ),
    },
  ], [canManage])

  return (
    <Page
      title="Approval policies"
      description="Rules that decide when an action needs approval and who needs to approve it."
      actions={canManage ? (
        <Button onClick={openCreate}><Plus className="mr-1 size-4" /> New policy</Button>
      ) : undefined}
    >
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : (data?.policies || []).length === 0 ? (
        <Card>
          <EmptyState
            icon={ShieldCheck}
            title="No approval policies yet"
            description="Create a policy to require approvals on invoice posts, refunds, write-offs, and more."
            action={canManage ? (
              <Button onClick={openCreate}><Plus className="mr-1 size-4" /> New policy</Button>
            ) : null}
          />
        </Card>
      ) : (
        <Card><div className="p-3"><DataTable columns={columns as never} rows={data?.policies ?? []} /></div></Card>
      )}

      <Modal
        open={editing !== null}
        onOpenChange={(o) => { if (!o) setEditing(null) }}
        title={editing?.mode === 'edit' ? `Edit policy: ${editing.policy.name}` : 'New approval policy'}
      >
        <div className="space-y-4">
          <FormField label="Name" required value={form.name} onChange={(e) => setField('name', e.currentTarget.value)} />
          <FormField
            label="Description"
            value={form.description || ''}
            onChange={(e) => setField('description', e.currentTarget.value)}
            hint="Shown in the inbox + audit log for context."
          />
          <div className="grid gap-3 md:grid-cols-2">
            <FormField
              label="Resource type"
              control={
                <select
                  className="h-10 w-full rounded-md border bg-card px-3 text-sm"
                  value={form.resource_type}
                  onChange={(e) => setField('resource_type', e.currentTarget.value as ApprovalResourceType)}
                >
                  {resourceTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              }
              hint="What kind of mutation this policy applies to."
            />
            <FormField
              label="Pattern"
              control={
                <select
                  className="h-10 w-full rounded-md border bg-card px-3 text-sm"
                  value={form.approval_pattern}
                  onChange={(e) => setField('approval_pattern', e.currentTarget.value as typeof form.approval_pattern)}
                >
                  {approvalPatterns.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              }
            />
            <FormField
              label="Priority"
              control={
                <NumberStepper
                  min={0}
                  value={form.priority == null ? '100' : String(form.priority)}
                  onChange={(v) => setField('priority', v === '' ? 100 : Number(v) || 100)}
                />
              }
              hint="Higher wins when multiple policies match."
            />
            <FormField
              label="SLA (hours)"
              control={
                <NumberStepper
                  min={0}
                  value={form.sla_hours == null ? '' : String(form.sla_hours)}
                  onChange={(v) => setField('sla_hours', v === '' ? null : Number(v))}
                />
              }
              hint="Leave blank for no deadline."
            />
            <FormField
              label="On SLA expiry"
              control={
                <select
                  className="h-10 w-full rounded-md border bg-card px-3 text-sm"
                  value={form.sla_action}
                  onChange={(e) => setField('sla_action', e.currentTarget.value as typeof form.sla_action)}
                >
                  {slaActions.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              }
            />
            <FormField
              label="Self-approval"
              control={
                <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-3">
                  <Checkbox
                    checked={!!form.self_approval_allowed}
                    onCheckedChange={(v) => setField('self_approval_allowed', v === true)}
                  />
                  <span className="text-sm text-muted-foreground">Allow the requester to approve their own request</span>
                </div>
              }
            />
          </div>

          <div>
            <div className="mb-2">
              <div className="text-sm font-semibold">Trigger conditions</div>
              <p className="text-xs text-muted-foreground">
                Leave blank to apply to every request of this resource.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <FormField
                label="Amount min"
                control={
                  <NumberStepper
                    min={0}
                    step={100}
                    value={form.trigger_conditions?.amount_min == null ? '' : String(form.trigger_conditions.amount_min)}
                    onChange={(v) => setField('trigger_conditions', {
                      ...form.trigger_conditions,
                      amount_min: v === '' ? undefined : Number(v),
                    })}
                  />
                }
              />
              <FormField
                label="Amount max"
                control={
                  <NumberStepper
                    min={0}
                    step={100}
                    value={form.trigger_conditions?.amount_max == null ? '' : String(form.trigger_conditions.amount_max)}
                    onChange={(v) => setField('trigger_conditions', {
                      ...form.trigger_conditions,
                      amount_max: v === '' ? undefined : Number(v),
                    })}
                  />
                }
              />
              <FormField
                label="Currency"
                control={
                  <div className="space-y-1">
                    <CurrencyCombobox
                      value={form.trigger_conditions?.currency || null}
                      onChange={(v) => setField('trigger_conditions', {
                        ...form.trigger_conditions,
                        currency: v || undefined,
                      })}
                    />
                    {form.trigger_conditions?.currency ? (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:underline"
                        onClick={() => setField('trigger_conditions', {
                          ...form.trigger_conditions,
                          currency: undefined,
                        })}
                      >
                        Clear (match any currency)
                      </button>
                    ) : null}
                  </div>
                }
                hint="Leave blank for any."
              />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold">Approval ladder</div>
              <Button size="sm" variant="secondary" onClick={addStep}>
                <Plus className="mr-1 size-4" /> Add step
              </Button>
            </div>
            <div className="space-y-2">
              {form.steps_config.map((s, i) => (
                <div key={i} className="grid gap-2 rounded-md border p-3 md:grid-cols-[1fr_120px_120px_40px]">
                  <FormField
                    label={`Step ${i + 1} - role`}
                    control={
                      <Combobox
                        value={s.role || null}
                        onChange={(v) => setStep(i, { role: v ? String(v) : '', user_id: undefined })}
                        options={roleOptions}
                        placeholder={rolesQ.isLoading ? 'Loading roles…' : 'Select a role'}
                        emptyMessage="No roles found."
                        onOpen={() => rolesQ.refetch()}
                      />
                    }
                    hint="System + custom roles from Team Management."
                  />
                  <FormField
                    label="Required"
                    control={
                      <NumberStepper
                        min={1}
                        value={String(s.required_count)}
                        onChange={(v) => setStep(i, { required_count: Math.max(1, Number(v) || 1) })}
                      />
                    }
                  />
                  <FormField
                    label="Parallel"
                    control={
                      <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-3">
                        <Checkbox checked={s.parallel} onCheckedChange={(v) => setStep(i, { parallel: v === true })} />
                      </div>
                    }
                  />
                  <div className="flex items-end justify-end">
                    {form.steps_config.length > 1 ? (
                      <Button size="icon" variant="ghost" className="size-9" onClick={() => removeStep(i)} aria-label="Remove step">
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Toolbar align="end">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={onSave} disabled={!policyDirty || createMut.isPending || updateMut.isPending}>Save</Button>
          </Toolbar>
        </div>
      </Modal>

      <Modal.Confirm
        open={deleteTarget !== null}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null) }}
        title={deleteTarget ? `Deactivate policy: ${deleteTarget.name}` : ''}
        body="The policy is deactivated (kept for audit references) rather than hard-deleted. Any pending instances keep their policy_snapshot intact."
        confirmLabel="Deactivate"
        confirmVariant="danger"
        busy={deleteMut.isPending}
        onConfirm={onDeleteConfirm}
      />
    </Page>
  )
}
